import { useState } from "react";
import { ErrorBoundary } from "@sentry/react";
import { Navigate } from "@tanstack/react-router";
import { Skeleton } from "antd5";
import { Flex, Text } from "styles/utility-components";

import { ErrorBoundaryFallback } from "components/app_layout/ErrorBoundaryFallback";
import Button from "components/ui/button/Button";
import { appRoutes } from "lib/appRoutes";
import { AiToneOfVoice, useAiToneOfVoices } from "lib/hooks/api/ai_tone_of_voice/useAiToneOfVoices";
import { useSubscription } from "lib/hooks/subscription";
import { Content, Page } from "../workflow_stages/shared";
import { DeleteToneOfVoiceModal } from "./DeleteToneOfVoiceModal";
import { ToneOfVoiceFormModal } from "./ToneOfVoiceFormModal";
import { ToneOfVoiceRow } from "./ToneOfVoiceRow";

export default function ToneOfVoicePage(): JSX.Element {
  const { hasDataTypes } = useSubscription();

  if (!hasDataTypes("BIDS")) {
    return <Navigate {...appRoutes.subscription} />;
  }

  return <ToneOfVoicePageContent />;
}

function ToneOfVoicePageContent(): JSX.Element {
  const { data: allEntries, isLoading } = useAiToneOfVoices();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AiToneOfVoice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AiToneOfVoice | null>(null);

  return (
    <Page>
      <Content>
        <ErrorBoundary showDialog fallback={(props) => <ErrorBoundaryFallback {...props} />}>
          <Flex column gap={24} width="100%">
            <Flex justifyContent="space-between" alignItems="center" width="100%">
              <Text h1>Tone of voice</Text>
              <Button onClick={() => setCreateOpen(true)} icon="plus">
                Create
              </Button>
            </Flex>

            <Flex column gap={8} width="100%">
              {isLoading ? (
                <Skeleton active />
              ) : (
                <>
                  {(allEntries ?? []).map((entry) => (
                    <ToneOfVoiceRow
                      key={entry.id}
                      entry={entry}
                      onEdit={() => setEditTarget(entry)}
                      onDelete={() => setDeleteTarget(entry)}
                    />
                  ))}
                </>
              )}
            </Flex>
          </Flex>

          <ToneOfVoiceFormModal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            mode="create"
            existingTovCount={allEntries?.length ?? 0}
          />
          {editTarget && (
            <ToneOfVoiceFormModal
              open={editTarget !== null}
              onClose={() => setEditTarget(null)}
              mode="edit"
              entry={editTarget}
            />
          )}
          {deleteTarget && (
            <DeleteToneOfVoiceModal
              open={deleteTarget !== null}
              onClose={() => setDeleteTarget(null)}
              entry={deleteTarget}
            />
          )}
        </ErrorBoundary>
      </Content>
    </Page>
  );
}
